import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Signal, effect, inject } from '@angular/core';

import { TableHeaderDefPipe } from 'src/app/components/generic-table/pipes/table-header-def.pipe';
import { ActionFn, ActionFunctions, TableDataSource, TableSorter, buildTable } from 'src/app/table-handling';
import { Table, TableConstructionData, TableRow } from 'src/app/table-handling/types';

import { LOG } from 'src/app/common/util/log';
import { TableSearcher } from 'src/app/table-handling/table-searcher';
import { AlignDirective, ColSpanDirective, RowspanDirective } from './directives';
import { TableHeaderDefSinglePipe, ValidRegularHeaderPipe } from './pipes';
import { TableCellComponent } from './table-cell/table-cell.component';
import { TableHeaderComponent } from './table-header/table-header.component';

@Component({
  selector: 'app-generic-table',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    CdkTableModule,

    TableHeaderDefPipe,
    TableHeaderDefSinglePipe,
    ValidRegularHeaderPipe,

    TableCellComponent,
    TableHeaderComponent,

    AlignDirective,
    RowspanDirective,
    ColSpanDirective,
  ],
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericTableComponent {
  @Input() public set tableData(tableConstructionData: TableConstructionData) {
    if (!tableConstructionData) {
      LOG.warn("Table construction data is undefined.");
      return;
    }
    this.constructTable(tableConstructionData);
  }

  @Input() public actionHandler: ActionFn | ActionFunctions | undefined;
  @Input({ required: false }) public searchTerm?: Signal<string | undefined>;

  protected dataSource: TableDataSource;
  protected sorter: TableSorter;
  protected table: Table;
  protected readonly searcher: TableSearcher = new TableSearcher();

  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      if (this.searchTerm) {
        this.searcher.search(this.searchTerm() ?? '');
        this.cdr.markForCheck();
      }
    })
  }

  protected isSeperatorVisible(index: number, data: TableRow): boolean {
    return true;
  }

  private constructTable(tableConstructionData: TableConstructionData): void {
    const [table, sorter] = buildTable(tableConstructionData, this.actionHandler);

    if (this.sorter) {
      sorter.copySortingFromOtherSorter(this.sorter);
    }

    this.sorter = sorter;
    this.searcher.table = table;
    this.searcher.restoreSearch();

    this.dataSource = new TableDataSource(table);

    sorter.onSort = () => {
      this.dataSource.next(table);
    };

    this.table = table;
  }
}
